import React, { useRef, useState } from 'react';
import { Camera, Upload } from 'lucide-react';
import { Button } from '../ui/Button';
import { useStore } from '../../store/useStore';
import { supabase } from '../../config/supabase';
import toast from 'react-hot-toast';

interface ProfilePictureProps {
  currentPicture: string;
  onPictureSelect: (picture: string) => void;
  name: string;
}

export const ProfilePicture: React.FC<ProfilePictureProps> = ({
  currentPicture,
  onPictureSelect,
  name,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadError, setUploadError] = useState<string>('');
  const { currentUser, updateUserProfile } = useStore();

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setUploadError('Please upload an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setUploadError('Image size should be less than 5MB');
      return;
    }

    setUploadError('');
    
    try {
      // Create storage bucket if it doesn't exist
      const { data: bucketData, error: bucketError } = await supabase
        .storage
        .getBucket('profile-pictures');

      if (bucketError && bucketError.message.includes('does not exist')) {
        await supabase
          .storage
          .createBucket('profile-pictures', {
            public: true,
            fileSizeLimit: 5242880, // 5MB
            allowedMimeTypes: ['image/jpeg', 'image/png', 'image/gif']
          });
      }

      // Upload to Supabase Storage
      const fileExt = file.name.split('.').pop();
      const fileName = `${currentUser?.id}/${Date.now()}.${fileExt}`;
      
      const { data, error } = await supabase.storage
        .from('profile-pictures')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: true
        });

      if (error) throw error;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('profile-pictures')
        .getPublicUrl(fileName);

      // Delete old profile picture if exists
      if (currentUser?.profile_picture?.fileName) {
        await supabase.storage
          .from('profile-pictures')
          .remove([currentUser.profile_picture.fileName]);
      }

      // Update profile with new picture data
      const pictureData = {
        url: publicUrl,
        fileName,
        contentType: file.type,
        updated_at: new Date().toISOString()
      };
      
      // Update both local state and database
      onPictureSelect(publicUrl);
      await updateUserProfile({ 
        profile_picture: pictureData,
        avatar: publicUrl // Keep avatar for backward compatibility
      });

      toast.success('Profile picture updated successfully');
    } catch (error) {
      console.error('Failed to upload profile picture:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to update profile picture';
      toast.error(errorMessage);
      setUploadError('Failed to upload image');
    }
  };

  return (
    <div className="flex flex-col items-center">
      <div className="relative mb-4">
        <img
          src={currentPicture}
          alt={name}
          className="w-24 h-24 rounded-full ring-4 ring-emerald-500 ring-offset-4 object-cover"
        />
        <button
          type="button"
          className="absolute bottom-0 right-0 p-2 bg-emerald-500 rounded-full text-white hover:bg-emerald-600 transition-colors"
          onClick={() => fileInputRef.current?.click()}
        >
          <Camera className="w-4 h-4" />
        </button>
      </div>

      <div className="w-full max-w-sm">
        <div className="flex flex-col items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => fileInputRef.current?.click()}
            icon={<Upload className="w-4 h-4" />}
          >
            Upload Photo
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileUpload}
            className="hidden"
          />
          {uploadError && (
            <p className="text-sm text-red-500">{uploadError}</p>
          )}
          <p className="text-xs text-gray-500 text-center mt-2">
            Supported formats: JPG, PNG, GIF (max. 5MB)
          </p>
        </div>
      </div>
    </div>
  );
};